// @flow

import React, { Fragment } from 'react';
import includes from 'lodash/includes';
import L10nMessage from 'react-aaui/lib/shared/L10nMessage';
import ResultCell from 'components/ResultCell/ResultCell';
import Row from 'react-aaui/lib/grid/Row';
import Col from 'components/Col/Col';
import ShowAt from 'components/Viewport/ShowAt';
import HideAt from 'components/Viewport/HideAt';
import type { StageResult, Rank } from 'type/type';
import { formatedDuration } from 'services/format/formatDuration';
import { pickRank } from 'services/rank/rank';

import './SportStageResult.scss';
import StageIcon from '../../StageIcon/StageIcon';
import { getStageResultPace } from '../../duck/selector';

type Props = {
  result: StageResult,
  rank: Rank[],
  name: string,
  type: string
}

const sportTypes = [
  'SWIMMING',
  'CYCLING',
  'RUNNING',
];

const DistanceCell = ({ result, distanceUnit }: { result: StageResult, distanceUnit: string }) => (
  <ResultCell
    label={
      <ShowAt xlAndAbove>
        <L10nMessage id="page.participant.summaryResult.distance" />
      </ShowAt>
    }
    content={
      result && result.distance ? (
        <Fragment>
          <span className="distance-data">
            {result.distance}
&nbsp;
          </span>
          <span className="unit">
            <L10nMessage id={`app.domain.result.distance.unit.${distanceUnit}`} />
          </span>
        </Fragment>
      ) : undefined
    } />
);

const PaceCell = ({ result, type, distanceUnit }: { result: StageResult, type: string, distanceUnit: string }) => {
  const paceData = getStageResultPace({ result: result || {}, type, distanceUnit });
  const displayedPaceUnit = paceData.unit && ` ${paceData.unit.trim()}`;

  return (
    <ResultCell
      label={
        <ShowAt xlAndAbove>
          <L10nMessage id="page.participant.summaryResult.pace" />
        </ShowAt>
      }
      content={
        paceData && paceData.value ? (
          <span className="pace-data">
            {paceData.value}
            <span className="unit pace-unit">
              {displayedPaceUnit}
            </span>
          </span>
        ) : undefined
      } />
  );
};

const SplitTimeCell = ({ result }: { result: StageResult }) => (
  <ResultCell
    label={<L10nMessage id="page.participant.splitReuslt.splitTime" />}
    content={
      result && result.splitTime
        ? formatedDuration(result.splitTime)
        : undefined
    } />
);

const TotalTimeCell = ({ totalTime }: { totalTime: string }) => (
  <ResultCell
    label={<L10nMessage id="page.participant.splitReuslt.totalTime" />}
    content={totalTime} />
);

const RankCol = ({ result, rank }: { result: StageResult, rank: Rank[] }) => {
  const sortedRanks = pickRank(rank) || [];

  return (
    <Col className="rank" span={12} sm={6} xl={4}>
      {result ? (
        <Row align="center" className="rank-row">
          <Col className="rank-cell" span={12}>
            {sortedRanks.map(r => (
              <div
                className="rank-cell-container"
                key={r.name}>
                <ResultCell
                  label={r.name}
                  content={r.rank} />
              </div>
            ))}
          </Col>
        </Row>
      ) : (
        ''
      )}
    </Col>
  );
};

const SportStageResult = ({
  name, result, rank, type,
}: Props) => {
  const distanceUnit = result && result.distanceUnit;
  return (
    <div className="participant-result-row sport-stage-result">
      <Row className="participant-result-row__content" gutter={0}>
        <Col className="stage-info" span={12} sm={12} xl={2}>
          <Row>
            <div className="stage-type-section">
              <StageIcon type={type} color="#fff" size="24px" wrapperClass="sport-stage-icon-wrapper" />
            </div>
            { type
              && <div className="name">
                {
                  includes(sportTypes, type)
                    ? <L10nMessage id={`page.participant.stageType.${type}`} />
                    : <span>{name}</span>
                }
              </div>
            }
          </Row>
        </Col>
        <ShowAt xlAndAbove>
          <Col className="result" xl={6}>
            <Row>
              <Col xl={3}>
                <DistanceCell result={result} distanceUnit={distanceUnit} />
              </Col>
              <Col xl={3}>
                <PaceCell result={result} type={type} distanceUnit={distanceUnit} />
              </Col>
              <Col xl={3}>
                <SplitTimeCell result={result} />
              </Col>
              <Col xl={3}>
                <TotalTimeCell totalTime={result && result.totalTime} />
              </Col>
            </Row>
          </Col>
        </ShowAt>
        <ShowAt xlAndAbove>
          <Col className="cut-item-line" />
          <RankCol result={result} rank={rank} />
        </ShowAt>
        <HideAt xlAndAbove>
          <ShowAt smAndAbove>
            <Col sm={6}>
              <Row justify="end" align="center" className="distance-pace-cell--mobile">
                <StageIcon type="distance" color="#b8b8b8" size="18px" wrapperClass="tablet-icon-wrapper" />
                <DistanceCell result={result} distanceUnit={distanceUnit} />
                <StageIcon type="pace" color="#b8b8b8" size="18px" wrapperClass="tablet-icon-wrapper" />
                <PaceCell result={result} type={type} distanceUnit={distanceUnit} />
              </Row>
            </Col>
          </ShowAt>
        </HideAt>
      </Row>

      <HideAt xlAndAbove>
        <ShowAt smAndAbove>
          <Row gutter={0}>
            <Col className="cut-item-row" />
          </Row>
          <Row align="center">
            <Col sm={3}>
              <SplitTimeCell result={result} />
            </Col>
            <Col className="cut-item-line" />
            <Col sm={3}>
              <TotalTimeCell totalTime={result && result.totalTime} />
            </Col>
            <Col className="cut-item-line" />
            <RankCol result={result} rank={rank} />
          </Row>
        </ShowAt>
      </HideAt>

      <HideAt smAndAbove>
        {
          result ? (
            <Fragment>
              <Row gutter={0}>
                <Col className="cut-item-row" />
              </Row>
              <Row className="race-duration--mobile" align="center">
                <Col className="split-time" span={6}>
                  <SplitTimeCell result={result} />
                </Col>
                <Col className="cut-item-line" />
                <Col className="total-time" span={6}>
                  <TotalTimeCell totalTime={result && result.totalTime} />
                </Col>
              </Row>
              <Row gutter={0}>
                <Col className="cut-item-row" />
              </Row>
              <Row gutter={0}>
                <RankCol result={result} rank={rank} />
              </Row>
            </Fragment>
          ) : null
        }
      </HideAt>
    </div>
  );
};

export default SportStageResult;
